import { addToWishlist, deleteFromWishlist, getWishlist, updateWishlistBtn } from '@components/wishlist';
import { execOnLoad, formatPrice, getA11yTranslations, lockPageInteraction, updateOmnibus } from '@general/helpers';
import breakpoints from 'breakpoints';
import { throttle } from 'lodash';
import Swiper from 'swiper';
import { A11y, Navigation } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';
import './quick-buy.scss';
import { initProductForm } from '@components/product';
import { closeAccordionWhenIdle, initAccordions, openAccordion } from '@components/accordions';
import { openMinicart } from '@components/minicart';

if (!window.dAgency) window.dAgency = {};
window.dAgency.initQuickBuy = (context) => {
  const snippetContainer = context;

  if (!snippetContainer) return;

  const priceWrapper = snippetContainer.querySelector('.quick-buy__price');

  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');

  // const BISButton = snippetContainer.querySelector('[data-bis-btn]');

  let swiperGalleryInstance = null;

  const initGallery = () => {
    const gallery = snippetContainer.querySelector('.quick-buy__gallery:not(.hidden) .quick-buy__gallery-medias');
    if (gallery) {
      // Destroy previous instance if exists
      if (swiperGalleryInstance && swiperGalleryInstance.destroy) {
        swiperGalleryInstance.destroy(true, true);
        swiperGalleryInstance = null;
      }

      const leftArrow = snippetContainer.querySelector(
        '.quick-buy__gallery:not(.hidden) .quick-buy__gallery-arrow--left',
      );
      const rightArrow = snippetContainer.querySelector(
        '.quick-buy__gallery:not(.hidden) .quick-buy__gallery-arrow--right',
      );

      const slides = gallery.querySelectorAll('.swiper-slide');
      const slidesCount = slides.length;

      const options = {
        loop: slidesCount > 1,
        modules: [...(window.dAgency.translations ? [A11y] : []), ...(leftArrow && rightArrow ? [Navigation] : [])],
        ...(leftArrow && rightArrow
          ? {
              navigation: {
                prevEl: leftArrow,
                nextEl: rightArrow,
              },
            }
          : {}),
        ...getA11yTranslations(),
      };

      if (gallery.dataset.desktopMode != 'carousel') {
        const lgUp = window.matchMedia(`(min-width: ${breakpoints.lg})`);
        const handleSwiper = () => {
          if (lgUp.matches) {
            if (swiperGalleryInstance && swiperGalleryInstance.destroy) swiperGalleryInstance.destroy(true, true);
          } else {
            swiperGalleryInstance = new Swiper(gallery, options);
          }
        };
        handleSwiper();
        lgUp.addEventListener('change', handleSwiper);
      } else {
        swiperGalleryInstance = new Swiper(gallery, options);
      }
    }
  };

  initGallery();

  // Open modal
  requestAnimationFrame(() => {
    snippetContainer.classList.add('open');
    lockPageInteraction(true, snippetContainer);
  });

  // Close and remove modal
  const closeBtn = snippetContainer.querySelector('.modal__close');
  const backdrop = snippetContainer.querySelector('.modal__backdrop');

  const handleEscape = (e) => {
    if (e.key === 'Escape') closeModal();
  };
  document.addEventListener('keydown', handleEscape);

  const closeModal = () => {
    document.removeEventListener('keydown', handleEscape);
    snippetContainer.classList.remove('open');
    lockPageInteraction(false, snippetContainer);

    // Waiting for transition end
    snippetContainer.addEventListener(
      'transitionend',
      () => {
        // Destroy Swiper before removing DOM node
        if (swiperGalleryInstance?.destroy) {
          swiperGalleryInstance.destroy(true, true);
          swiperGalleryInstance = null;
        }
        snippetContainer?.parentNode?.removeChild(snippetContainer);
      },
      { once: true },
    );
  };

  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (backdrop) backdrop.addEventListener('click', closeModal);

  const form = snippetContainer.querySelector('.quick-buy__form');
  const galleryWrapper = snippetContainer.querySelector('.quick-buy__gallery-wrapper');

  if (form) {
    const errorWrapper = form.querySelector('.quick-buy__error-wrapper');
    const errorMessage = form.querySelector('.quick-buy__error [data-message]');
    const submit = form.querySelector('[type="submit"]');

    // Size dropdown, as on the product page. The quick buy is injected after
    // main.js ran initAccordions, so it has to be wired here.
    const sizeSelect = form.querySelector('.quick-buy__size-select');
    if (sizeSelect) {
      initAccordions(sizeSelect.parentElement);
      sizeSelect.addEventListener('change', () => closeAccordionWhenIdle(sizeSelect));
    }

    // No size is preselected, so the id field stays empty until one is picked:
    // block the submit, say what is missing and open the dropdown. The error is
    // cleared by afterVariantChange.
    if (submit && sizeSelect) {
      submit.addEventListener('click', (event) => {
        if (sizeSelect.querySelector('input:checked')) return;
        event.preventDefault();

        if (errorWrapper && errorMessage) {
          errorMessage.innerText = sizeSelect.dataset.error || '';
          errorWrapper.classList.add('shown');
        }

        openAccordion(sizeSelect);
        sizeSelect.querySelector(':scope > summary')?.focus();
      });
    }

    initProductForm({
      form,
      callbacks: {
        afterVariantChange: async ({ variant, product }) => {
          // Drives the sold out look, which must show even when back in stock
          // keeps the input enabled: availability depends on the other options.
          const options = variant.options.reduce(
            (acc, option, index) => ({
              ...acc,
              [`option${index + 1}`]: option,
            }),
            {},
          );
          form.querySelectorAll('.quick-buy__option-value input').forEach((input) => {
            const currentOptions = {
              ...options,
              [input.name]: input.value,
            };
            const foundVariant = product.variants.find((productVariant) =>
              Object.keys(currentOptions).every(
                (optionName) => productVariant[optionName] == currentOptions[optionName],
              ),
            );
            input.dataset.available = String(!!foundVariant?.available);
          });

          // The closed dropdown shows the picked size, greyed with its note
          // when sold out.
          if (sizeSelect) {
            const sizeIndex = Number(sizeSelect.closest('fieldset').id.replace('option', '')) - 1;
            const selectLabel = sizeSelect.querySelector('[data-size-select-label]');
            if (selectLabel) selectLabel.innerText = variant.options[sizeIndex];
            const selectValue = sizeSelect.querySelector('[data-size-select-value]');
            if (selectValue) selectValue.dataset.soldOut = String(!variant.available);
          }
          errorWrapper?.classList.remove('shown');

          if (wishlistButton) {
            wishlistButton.dataset.wishlistVariantId = variant?.id;
            wishlistButton.dataset.wishlistPrice = formatPrice({
              value: variant?.price ?? 0,
            });
            wishlistButton.dataset.wishlistCompareAtPrice = variant?.compare_at_price
              ? formatPrice({
                  value: variant?.compare_at_price,
                })
              : '';

            const wishlist = await getWishlist();
            const wishlistProduct = wishlist?.listcontents?.find(
              (addedProduct) => addedProduct.epi == variant.id && addedProduct.empi == product.id,
            );
            wishlistButton.dataset.wishlistStatus = wishlistProduct ? 'active' : 'inactive';
          }
        },
        beforeSubmit: () => {
          const uniqueIdField = form.querySelector('.js-unique-id');
          if (uniqueIdField) {
            uniqueIdField.value = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          }
        },
        afterSubmit: () => {
          const quickBuyClose = snippetContainer.querySelector('.quick-buy__container.open .modal__close');
          if (quickBuyClose) quickBuyClose.click();
          openMinicart(true);
        },
        errorCallback: (err) => {
          if (errorWrapper && errorMessage && err?.data?.message) {
            errorMessage.innerText = err.data.message;
            requestAnimationFrame(() => {
              errorWrapper.classList.add('shown');
              setTimeout(() => {
                errorWrapper.classList.remove('shown');
              }, 3000);
            });
          }
        },
      },
    });

    if (typeof window?.dAgency?.initCarousel == 'function') window.dAgency.initCarousel(galleryWrapper);

    updateOmnibus(snippetContainer, priceWrapper);

    // Update option label on change
    form.addEventListener('change', (e) => {
      if (!e.target.matches('input[type="radio"]')) return;

      const optionWrapper = e.target.closest('.quick-buy__option');
      if (!optionWrapper) return;

      const currentValue = optionWrapper.querySelector('[data-current-value]');
      if (currentValue) {
        currentValue.textContent = e.target.value;
      }
    });
  }

  if (wishlistButton) {
    updateWishlistBtn(wishlistButton);

    const toggleLoading = throttle(() => {
      wishlistButton.classList.toggle('loading');
    }, 500);

    wishlistButton.addEventListener('click', async (e) => {
      console.log('wishlist quick buy');
      e.preventDefault();
      toggleLoading();

      const variantID = wishlistButton?.dataset?.wishlistVariantId;
      const masterID = wishlistButton?.dataset?.wishlistProductId;
      const productURL = wishlistButton?.dataset?.wishlistProductUrl;
      const price = wishlistButton?.dataset?.wishlistPrice;
      const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

      if (wishlistButton?.dataset?.wishlistStatus === 'active') {
        await deleteFromWishlist({ variantID, masterID, productURL, button: wishlistButton });
      } else {
        await addToWishlist({
          variantID,
          masterID,
          productURL,
          compareAtPrice,
          price,
          button: wishlistButton,
        });
      }
      toggleLoading();
    });
  }
};

execOnLoad(window.dAgency.initQuickBuy, '.quick-buy__container');
